"use client";

import { useRef } from "react";

import { cn } from "../lib/cn";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
  /** Drawn before the label, for a choice that is easier to spot by shape (a table, a map). */
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
};

type SegmentedControlProps<T extends string> = {
  value: T;
  options: readonly SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  /** Names the choice for assistive tech, e.g. "Brand". */
  label: string;
  /** Also render the label beside the control when the surrounding UI needs it. */
  visibleLabel?: boolean;
  className?: string;
};

/**
 * One track, one filled segment: a mutually exclusive choice among a handful of
 * short options. Reach for it instead of a row of outlined buttons: a set of
 * separately bordered pills reads as several independent toggles and spends one
 * outline per option on chrome that says nothing.
 *
 * A radiogroup rather than a set of pressed buttons: the options are one choice,
 * so a screen reader should announce "2 of 4", and the arrow keys should move
 * between them while Tab leaves the group.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  visibleLabel = false,
  className,
}: SegmentedControlProps<T>) {
  // The rendered buttons, by index. Focus moves through this rather than through a
  // selector built from the option's value: a value is a caller's string, and one
  // holding a quote would make that selector throw rather than move focus.
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving arrow keys, per the radiogroup pattern: moving the selection is what
  // moves focus, so the group holds exactly one tab stop.
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    const index = options.findIndex((option) => option.value === value);
    // A value the options do not hold is not a selection to move from, so there is
    // no "next" that means anything. Leave the key to the page.
    if (index < 0) return;
    event.preventDefault();
    const nextIndex = (index + step + options.length) % options.length;
    onChange(options[nextIndex].value);
    buttonsRef.current[nextIndex]?.focus();
  }

  const control = (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={cn(
        "inline-flex h-8 items-center rounded-md border border-border bg-surface p-0.5",
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              buttonsRef.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={cn(
              "h-full cursor-pointer appearance-none rounded border-0 bg-transparent px-2.5 font-[inherit] text-sm transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
              option.icon && "inline-flex items-center gap-1.5",
              selected
                ? "bg-surface-raised font-medium text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.icon && <option.icon aria-hidden className="size-3.5 shrink-0" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );

  if (!visibleLabel) return control;

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {control}
    </div>
  );
}
